import * as Placeholder from '-/Custom/Placeholder';
import { Link, useRouter } from 'expo-router';
import { useRef } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import BadgeListItem, {
  BadgeListItemFragment,
} from '@/components/Badge/BadgeListItem';
import Paginator from '@/components/Custom/Paginator';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import { graphql, readFragment } from '@/gql/graphql';
import usePaginatedList from '@/hooks/usePaginatedList';
import useRootStore from '@/stores/useRootStore';

const BADGE_PAGE_LIMIT = 24;

const GetOrganizationBadgeList = graphql(/* GraphQL */ `
  query GetOrganizationBadgeList(
    $organizationId: uuid!
    $badgeAssigneesWhere: badge_assignee_bool_exp!
    $includeBadgeAssignees: Boolean!
    $badgeLimit: Int!
    $badgeOffset: Int!
  ) {
    event_by_pk(id: $organizationId) {
      id
      root_owner {
        id
        title
        permissions
      }
    }
    relevant_badge(
      args: { event_id: $organizationId }
      limit: $badgeLimit
      offset: $badgeOffset
      order_by: { title: asc }
    ) {
      id
      ...BadgeListItem
    }
    relevant_badge_aggregate(args: { event_id: $organizationId }) {
      aggregate {
        count
      }
    }
  }
`, [BadgeListItemFragment]);

export default function OrganizationDetailBadges() {
  const { push } = useRouter();
  const organizationId = useRootStore((state) => state.organizationId);
  const scrollViewRef = useRef<ScrollView>(null);
  const paginated = usePaginatedList({
    limit: BADGE_PAGE_LIMIT,
    scrollViewRef,
  });
  const variables = {
    organizationId: organizationId || '',
    badgeAssigneesWhere: {
      event_id: { _eq: organizationId },
    },
    includeBadgeAssignees: true,
    badgeLimit: paginated.limit,
    badgeOffset: paginated.offset,
  };
  const [{ data, fetching }] = useQuery({
    query: GetOrganizationBadgeList,
    variables,
    pause: !organizationId,
  });

  const rootParent = data?.event_by_pk?.root_owner?.[0];
  const badges = data?.relevant_badge ?? [];
  const badgeCount = data?.relevant_badge_aggregate.aggregate?.count ?? 0;

  return (
    <FadeInContent fetching={fetching}>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.content}
        contentInsetAdjustmentBehavior="automatic"
      >
        {rootParent?.permissions?.edit_badge ? (
          <Box direction="horizontal" space="md" py="md" px="lg">
            <Link
              href={{
                pathname: '/badge/create',
                params: { eventId: rootParent.id },
              }}
              asChild
            >
              <ActionButton
                forceLabel
                label="Create Badge"
                icon="add-outline"
                variant="success"
              />
            </Link>
          </Box>
        ) : null}

        {badges.length ? (
          <Box direction="vertical" testID="OrganizationBadgeList">
            {badges.map((badge) => {
              const badgeItem = readFragment(BadgeListItemFragment, badge);

              return (
                <Item
                  key={badgeItem.id}
                  testID={`OrganizationBadgeListItem-${badgeItem.id}`}
                  onPress={() => {
                    push({
                      pathname: '/organization/badge/[badgeId]',
                      params: {
                        badgeId: badgeItem.id,
                        eventId: organizationId || '',
                      },
                    });
                  }}
                >
                  <BadgeListItem fragment={badge} />
                </Item>
              );
            })}
          </Box>
        ) : (
          <Placeholder.Container>
            <Box style={styles.placeholderIcon}>
              <Placeholder.Icon name="grid-outline" />
            </Box>
            <Placeholder.Text>
              {rootParent?.title} Has No Badges.
            </Placeholder.Text>
            <Placeholder.Text>
              Badges can be used to track registration, waivers, and roster
              details.
            </Placeholder.Text>
          </Placeholder.Container>
        )}

        <Paginator {...paginated} total={badgeCount} />
      </ScrollView>
    </FadeInContent>
  );
}

const styles = StyleSheet.create((theme) => ({
  content: {
    flexGrow: 1,
    gap: theme.space('lg'),
    paddingBottom: theme.space('xl'),
  },
  placeholderIcon: {
    transform: [{ rotate: '45deg' }],
  },
}));
